import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Flag, Loader2, MessageSquare, Star } from "lucide-react";
import { toast } from "sonner";
import BackOffice from "@/components/account/BackOffice";
import { Btn, Chip, PageHead, Pill, Section, inputCls } from "@/components/account/Shell";
import api, { formatApiError } from "@/lib/api";

export const Stars = ({ n, size = 12, testid }) => (
    <span className="inline-flex items-center gap-px" data-testid={testid} aria-label={`${n} out of 5`}>
        {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={size} strokeWidth={2}
                className={i <= n ? "fill-[#b54708] text-[#b54708]" : "text-[#d0d0d0]"} />
        ))}
    </span>
);

const day = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } };

function Tile({ label, value, note, testid, children }) {
    return (
        <div className="border border-[#d0d0d0] bg-white p-2.5" data-testid={testid}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">{label}</p>
            <p className="mt-1 flex items-center gap-1.5 text-xl font-bold tabular-nums text-[#1a1a1a]">
                {value}{children}
            </p>
            {note && <p className="mt-0.5 text-[12px] text-[#4a4a4a]">{note}</p>}
        </div>
    );
}

function Row({ r, onReply, onReport, busy }) {
    const [draft, setDraft] = useState(r.reply?.body || "");
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    useEffect(() => setDraft(r.reply?.body || ""), [r.reply]);

    return (
        <div className="border-t border-[#d0d0d0] px-2.5 py-2 first:border-t-0" data-testid={`review-${r.id}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Stars n={r.rating} testid={`review-stars-${r.id}`} />
                <span className="text-sm font-bold text-[#1a1a1a]">{r.title || `${r.rating} out of 5`}</span>
                {r.verified && <Chip tone="green" testid={`review-verified-${r.id}`}>verified order</Chip>}
                {r.reported && <Chip tone="amber" testid={`review-reported-${r.id}`}>reported</Chip>}
                {r.hidden && <Chip tone="red" testid={`review-hidden-${r.id}`}>hidden by 13thav</Chip>}
                <span className="ml-auto text-[11px] font-semibold uppercase text-[#4a4a4a]">{day(r.created_at)}</span>
            </div>
            <p className="mt-1 text-[13px] leading-snug text-[#1a1a1a]">{r.body}</p>
            <p className="mt-1 text-[12px] text-[#4a4a4a]">
                {r.author} · {r.product_name}
                {r.order_number ? ` · order ${r.order_number}` : ""}
            </p>

            <div className="mt-1.5 flex flex-wrap items-start gap-1.5">
                <textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)}
                    placeholder="Reply in public — customers read this" data-testid={`review-reply-${r.id}`}
                    className={`${inputCls} min-w-[16rem] flex-1`} />
                <div className="flex flex-col gap-1">
                    <Btn tone="solid" disabled={busy || draft === (r.reply?.body || "")}
                        testid={`review-reply-save-${r.id}`} onClick={() => onReply(r, draft)}>
                        <MessageSquare size={11} /> {r.reply ? "Update reply" : "Post reply"}
                    </Btn>
                    {!r.reported && (
                        <Btn testid={`review-report-${r.id}`} onClick={() => setOpen((v) => !v)}>
                            <Flag size={11} /> Report
                        </Btn>
                    )}
                </div>
            </div>
            {r.reply && (
                <p className="mt-1 border-l-2 border-[#1a1a1a] bg-[#fafafa] px-2 py-1 text-[12px] text-[#1a1a1a]"
                    data-testid={`review-reply-live-${r.id}`}>
                    <span className="font-semibold">Your reply, live: </span>{r.reply.body}
                </p>
            )}
            {open && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 border border-[#d0d0d0] bg-[#fafafa] p-1.5">
                    <input value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Why should 13thav support look at this?"
                        data-testid={`review-report-why-${r.id}`} className={`${inputCls} min-w-[14rem] flex-1`} />
                    <Btn tone="danger" disabled={busy} testid={`review-report-send-${r.id}`}
                        onClick={() => { onReport(r, reason); setOpen(false); setReason(""); }}>
                        Send to 13thav support
                    </Btn>
                    <span className="text-[11px] text-[#4a4a4a]">A shop can't delete a review — only 13thav support can.</span>
                </div>
            )}
        </div>
    );
}

const FILTERS = [
    ["all", "All"], ["unanswered", "Unanswered"], ["answered", "Answered"], ["reported", "Reported"],
    ["5", "5 star"], ["4", "4 star"], ["3", "3 star"], ["2", "2 star"], ["1", "1 star"],
];

export default function Reviews() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [filter, setFilter] = useState("all");
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => api.get("/store/reviews").then(({ data: d }) => setData(d))
        .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Could not open your reviews")), []);
    useEffect(() => { load(); }, [load]);

    const reply = async (r, body) => {
        setBusy(true);
        try { await api.post(`/store/reviews/${r.id}/reply`, { body }); toast.success(body ? "Reply posted" : "Reply removed"); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const report = async (r, reason) => {
        setBusy(true);
        try { await api.post(`/store/reviews/${r.id}/report`, { reason }); toast.success("Sent to 13thav support"); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const rows = useMemo(() => (data?.reviews || []).filter((r) => {
        if (filter === "all") return true;
        if (filter === "unanswered") return !r.reply;
        if (filter === "answered") return !!r.reply;
        if (filter === "reported") return r.reported;
        return String(r.rating) === filter;
    }), [data, filter]);

    if (err) return (
        <BackOffice><div className="mx-auto max-w-2xl py-20 text-center" data-testid="reviews-locked">
            <Star size={34} className="mx-auto text-[#4a4a4a]" />
            <h1 className="mt-4 text-xl font-bold text-[#1a1a1a]">Reviews come with a space.</h1>
            <p className="mt-2 text-sm text-[#4a4a4a]">{err}</p>
            <Link to="/account/sell" className="mt-5 inline-block"><Btn tone="solid" testid="reviews-open-space">See the spaces</Btn></Link>
        </div></BackOffice>
    );

    if (!data) return (
        <BackOffice><div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="animate-spin text-[#4a4a4a]" /></div></BackOffice>
    );

    const s = data.stats;
    return (
        <BackOffice>
            <PageHead kicker="Messages" title="Reviews" testid="reviews-head"
                sub="Only a customer with a finished job can write one. Reply in public — that answer sells the next job."
                right={<Link to={`/stores/${data.store.slug}`} data-testid="reviews-view-store">
                    <Btn>See them on my store</Btn></Link>} />

            <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4" data-testid="reviews-tiles">
                <Tile label="Star average" value={s.average || "—"} testid="reviews-average"
                    note={`${s.count} review${s.count === 1 ? "" : "s"}`}>
                    <Stars n={Math.round(s.average)} size={13} />
                </Tile>
                <Tile label="Five stars" value={`${s.five_pct}%`} testid="reviews-five"
                    note={`${s.spread["5"]} of ${s.count || 0}`} />
                <Tile label="Waiting on a reply" value={s.unanswered} testid="reviews-unanswered"
                    note="every reply is public" />
                <Tile label="Replied" value={s.answered} testid="reviews-answered" note="thank the good ones too" />
            </div>

            <div className="mt-2">
                <Section title="How they score you" testid="reviews-spread">
                    <div className="space-y-1">
                        {[5, 4, 3, 2, 1].map((n) => {
                            const v = s.spread[String(n)] || 0;
                            const pct = s.count ? Math.round((v / s.count) * 100) : 0;
                            return (
                                <div key={n} className="flex items-center gap-2" data-testid={`reviews-bar-${n}`}>
                                    <span className="w-16 shrink-0"><Stars n={n} size={10} /></span>
                                    <span className="h-3 flex-1 border border-[#d0d0d0] bg-white">
                                        <span className="block h-full bg-[#1a1a1a]" style={{ width: `${pct}%` }} />
                                    </span>
                                    <span className="w-16 shrink-0 text-right text-[12px] font-semibold tabular-nums text-[#1a1a1a]">
                                        {v} · {pct}%
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </Section>
            </div>

            <div className="mt-2">
                <Section title="Every review" pad={false} testid="reviews-list"
                    note={`${rows.length} shown`}
                    right={
                        <div className="flex flex-wrap gap-1">
                            {FILTERS.map(([k, label]) => (
                                <Pill key={k} active={filter === k} testid={`reviews-filter-${k}`}
                                    onClick={() => setFilter(k)}>{label}</Pill>
                            ))}
                        </div>
                    }>
                    {rows.length ? rows.map((r) => (
                        <Row key={r.id} r={r} onReply={reply} onReport={report} busy={busy} />
                    )) : (
                        <p className="p-3 text-sm text-[#4a4a4a]" data-testid="reviews-empty">
                            Nothing here yet. Reviews open up the moment you mark a job ready or picked up.
                        </p>
                    )}
                </Section>
            </div>
        </BackOffice>
    );
}
